/* eslint-disable max-lines-per-function */
import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'

import type { AuthResponse, UserBasic, Inscription } from '@/types/auth'

interface AuthStore {
    user: UserBasic | null
    idAspirant: number | null
    idPaymentOrder: number | null
    accessToken: string | null
    refreshToken: string | null
    isAuthenticated: boolean
    firstName: string | null
    lastName: string | null
    email: string | null
    cellphone: string | null
    isAgent: boolean
    idAsesor: number | null
    isMinor: boolean | null
    studyModality: string | null
    setAuth: (auth: AuthResponse) => void
    updateUserData: (
        firstName: string,
        lastName: string,
        cellphone: string
    ) => void
    updateUserInfo: (userInfo: {
        firstName?: string
        lastName?: string
        email?: string
        cellphone?: string
    }) => void
    updateIsMinor: (isMinor: boolean | null) => void
    clearAuth: () => void
    updateTokens: ({
        accessToken,
        refreshToken
    }: {
        accessToken: string
        refreshToken: string
    }) => void
    updateIdInscription: (idInscription: number | null) => void
    updateStudyModality: (studyModality: string) => void
    idInscription: number | null
    inscriptions: Inscription[]
    /** Sincroniza la lista de inscripciones (p. ej. tras crear una nueva vía API) */
    setInscriptions: (inscriptions: Inscription[]) => void
    programName: string | null
    updateIdPaymentOrder: (idPaymentOrder: number) => void
}

// Create a custom storage that handles agent vs aspirant storage differences
const createConditionalStorage = () => {
    return {
        getItem: (name: string) => {
            try {
                // Agent storage check (sessionStorage with prefix)
                const agentData = sessionStorage.getItem(`agent-${name}`)

                if (agentData) {
                    return agentData
                }

                // Fall back to aspirant storage (regular localStorage)
                const localData = localStorage.getItem(name)

                return localData
            } catch (e) {
                console.error('Error reading from storage', e)

                return null
            }
        },
        setItem: (name: string, value: string) => {
            try {
                // Parse the value to check if isAgent flag is set
                const parsedValue = JSON.parse(value)
                const isAgent = parsedValue?.state?.isAgent

                if (isAgent) {
                    // Store agent data in sessionStorage with special prefix
                    sessionStorage.setItem(`agent-${name}`, value)

                    // Debugging: validate the storage worked
                    const storedValue = sessionStorage.getItem(`agent-${name}`)

                    if (storedValue !== value) {
                        console.error('Agent storage validation failed')
                    }
                } else {
                    // Store regular user data in localStorage
                    localStorage.setItem(name, value)

                    // Debugging: validate the storage worked
                    const storedValue = localStorage.getItem(name)

                    if (storedValue !== value) {
                        console.error('Aspirant storage validation failed')
                    }
                }
            } catch (e) {
                console.error('Error writing to storage', e)
                // Default to localStorage if parsing fails
                localStorage.setItem(name, value)
            }
        },
        removeItem: (name: string) => {
            try {
                // Clear from both storages to be safe
                sessionStorage.removeItem(`agent-${name}`)
                localStorage.removeItem(name)
            } catch (e) {
                console.error('Error removing from storage', e)
            }
        }
    }
}

export const useAuthStore = create<AuthStore>()(
    persist(
        (set) => ({
            user: null,
            idAspirant: null,
            idPaymentOrder: null,
            accessToken: null,
            refreshToken: null,
            isAuthenticated: false,
            firstName: null,
            lastName: null,
            email: null,
            cellphone: null,
            isAgent: false,
            idAsesor: null,
            isMinor: null,
            studyModality: null,
            idInscription: null,
            inscriptions: [],
            programName: null,
            setAuth: (auth) => {
                console.log(
                    'Setting auth with isAgent:',
                    auth.idAsesor !== null
                )

                set({
                    user: auth.user,
                    idAspirant: auth.idAspirant,
                    idPaymentOrder: auth.idPaymentOrder,
                    accessToken: auth.token.access_token,
                    refreshToken: auth.token.refresh_token,
                    firstName: auth.firstName,
                    lastName: auth.lastName,
                    email: auth.email,
                    cellphone: auth.cellphone,
                    isAgent: auth.idAsesor !== null,
                    idAsesor: auth.idAsesor,
                    //isAgent: true,

                    idInscription:
                        auth.inscriptions?.find((i) => i.isActive)?.id ??
                        auth.inscriptions?.[0]?.id ??
                        null,
                    inscriptions: auth.inscriptions ?? [],
                    programName: null, // programName no viene en la nueva estructura
                    isAuthenticated: true
                })
            },
            // Method to update specific user info fields
            updateUserData: (firstName, lastName, cellphone) => {
                set((state) => {
                    // Update user object if it exists
                    if (state.user) {
                        state.user.firstName = firstName
                        state.user.lastName = lastName
                    }

                    return {
                        ...state,
                        firstName,
                        lastName,
                        cellphone
                    }
                })
                // Also update individual storage items
                const isAgent = useAuthStore.getState().isAgent

                if (isAgent) {
                    // Agent storage
                    sessionStorage.setItem(
                        'agent-firstName',
                        JSON.stringify(firstName)
                    )
                    sessionStorage.setItem(
                        'agent-lastName',
                        JSON.stringify(lastName)
                    )
                    sessionStorage.setItem(
                        'agent-cellphone',
                        JSON.stringify(cellphone)
                    )
                } else {
                    // Aspirant storage
                    localStorage.setItem('firstName', JSON.stringify(firstName))
                    localStorage.setItem('lastName', JSON.stringify(lastName))
                    localStorage.setItem('cellphone', JSON.stringify(cellphone))
                }
            },
            // More flexible method to update any combination of user info fields
            updateUserInfo: (userInfo) => {
                set((state) => {
                    const updates: Partial<AuthStore> = {}

                    // Update firstName if provided
                    if (userInfo.firstName !== undefined) {
                        updates.firstName = userInfo.firstName
                        if (state.user) {
                            state.user.firstName = userInfo.firstName
                        }
                    }

                    // Update lastName if provided
                    if (userInfo.lastName !== undefined) {
                        updates.lastName = userInfo.lastName
                        if (state.user) {
                            state.user.lastName = userInfo.lastName
                        }
                    }

                    // Update email if provided
                    if (userInfo.email !== undefined) {
                        updates.email = userInfo.email
                        if (state.user) {
                            state.user.email = userInfo.email
                        }
                    }

                    // Update cellphone if provided
                    if (userInfo.cellphone !== undefined) {
                        updates.cellphone = userInfo.cellphone
                    }

                    return {
                        ...state,
                        ...updates
                    }
                })
            },
            updateIsMinor: (isMinor) =>
                set((state) => ({
                    ...state,
                    isMinor
                })),
            updateTokens: ({ accessToken, refreshToken }) =>
                set((state) => {
                    console.log('Updating tokens with isAgent:', state.isAgent)

                    // Store tokens in the appropriate storage based on user type
                    if (state.isAgent) {
                        sessionStorage.setItem('accessToken', accessToken)
                        sessionStorage.setItem('refreshToken', refreshToken)
                    } else {
                        localStorage.setItem('accessToken', accessToken)
                        localStorage.setItem('refreshToken', refreshToken)
                    }

                    return {
                        ...state,
                        accessToken,
                        refreshToken
                    }
                }),

            updateIdInscription: (idInscription) =>
                set((state) => {
                    console.log('Updating idInscription:', idInscription)

                    return {
                        ...state,
                        idInscription,
                        idPaymentOrder: null // Reset payment order when inscription changes
                    }
                }),
            setInscriptions: (inscriptions) =>
                set((state) => ({
                    ...state,
                    inscriptions
                })),

            updateStudyModality: (studyModality: string) =>
                set((state) => {
                    // Log simplificado solo en desarrollo
                    if (process.env.NODE_ENV === 'development') {
                        console.log(
                            `🔄 AuthStore: studyModality "${state.studyModality}" → "${studyModality}"`
                        )
                    }

                    return {
                        ...state,
                        studyModality
                    }
                }),

            // ← Add the missing updateIdPaymentOrder method
            updateIdPaymentOrder: (idPaymentOrder: number | null) =>
                set((state) => {
                    console.log('Updating idPaymentOrder:', idPaymentOrder)

                    return {
                        ...state,
                        idPaymentOrder
                    }
                }),

            clearAuth: () => {
                // Clear tokens from both storage types
                localStorage.removeItem('accessToken')
                localStorage.removeItem('refreshToken')
                sessionStorage.removeItem('accessToken')
                sessionStorage.removeItem('refreshToken')

                // Also clear prefixed agent tokens
                sessionStorage.removeItem('agent-accessToken')
                sessionStorage.removeItem('agent-refreshToken')

                set({
                    user: null,
                    idAspirant: null,
                    idPaymentOrder: null, // ← Add this line
                    accessToken: null,
                    refreshToken: null,
                    firstName: null,
                    lastName: null,
                    email: null,
                    cellphone: null,
                    isAgent: false,
                    idAsesor: null,
                    isMinor: null,
                    studyModality: null,
                    isAuthenticated: false,
                    idInscription: null,
                    inscriptions: [],
                    programName: null
                })
            }
        }),
        {
            name: 'auth-storage',
            storage: createJSONStorage(() => createConditionalStorage()),
            // Add helpful debugging for storage issues
            onRehydrateStorage: () => (state) => {
                if (state) {
                    console.log(
                        'Auth store rehydrated with isAgent:',
                        state.isAgent
                    )
                } else {
                    console.error('Failed to rehydrate auth store')
                }
            }
        }
    )
)
